/**
 * Reply-classification vocabulary — the frontend mirror of
 * `backend/app/services/reply_vocabulary.py`. Keep the two in step.
 *
 * Every reply carries TWO independent tags (Reply Module spec §1.2–§1.3):
 *   sentiment — how the person feels  (3 canonical values)
 *   intent    — what they want next   (8 canonical values)
 *
 * The database still stores the older 6-value sentiment list, so anything that
 * DISPLAYS a stored sentiment must map it through `canonicalSentiment` first.
 * Otherwise a card reads "Interested" while the filter that returned it says
 * "Positive" — the same row described two different ways on one screen.
 */

export type CanonicalSentiment = 'positive' | 'neutral' | 'negative'

/**
 * Stored sentiment label -> the canonical tone it belongs to.
 *
 * `interested` is tentative warmth, which is positive TONE; the engagement
 * nuance survives on the intent axis as intent='interested'.
 * `not_interested` maps to neutral, per spec §1.2 — a polite decline is
 * neutral in tone, and the declining part is carried by intent.
 * `out_of_office` maps to nothing: it is a delivery class, not a human tone.
 */
const STORED_TO_CANONICAL: Record<string, CanonicalSentiment> = {
  positive: 'positive',
  interested: 'positive',
  neutral: 'neutral',
  not_interested: 'neutral',
  negative: 'negative',
}

/** Canonical tone for a stored sentiment, or null when it carries no human tone. */
export function canonicalSentiment(stored: string | null | undefined): CanonicalSentiment | null {
  if (!stored) return null
  return STORED_TO_CANONICAL[stored] ?? null
}

export const SENTIMENT_LABELS: Record<CanonicalSentiment, string> = {
  positive: 'Positive',
  neutral: 'Neutral',
  negative: 'Negative',
}

/** Chip styling per tone — border + soft fill pair, dual-mode (§31 / §22). */
export const SENTIMENT_CHIP_CLASS: Record<CanonicalSentiment, string> = {
  positive:
    'bg-emerald-500/10 text-emerald-700 border-emerald-500/25 dark:bg-emerald-500/[0.12] dark:text-emerald-400 dark:border-emerald-500/25',
  neutral:
    'bg-muted/60 text-muted-foreground border-border/50 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06]',
  negative:
    'bg-red-500/10 text-red-700 border-red-500/25 dark:bg-red-500/[0.12] dark:text-red-400 dark:border-red-500/25',
}

/**
 * Intent labels. The first eight are the spec's canonical set; `auto_reply` and
 * `out_of_office` are reply KINDS that exist in stored data and must still
 * render a readable chip, so they are labelled here but are absent from the
 * Intent filter's options.
 *
 * `other` displays as "None" — the spec's name for the catch-all. Renaming the
 * stored value belongs to the later data migration.
 */
export const INTENT_LABELS: Record<string, string> = {
  meeting_request: 'Meeting request',
  information_request: 'Information request',
  interested: 'Interested',
  objection: 'Objection',
  referral: 'Referral',
  not_interested: 'Not interested',
  unsubscribe: 'Unsubscribe',
  other: 'None',
  auto_reply: 'Auto-reply',
  out_of_office: 'Out of office',
}

/**
 * Options for the Inbox's Intent filter, in display order.
 *
 * Deliberately NOT derived from INTENT_LABELS: that map also carries the reply
 * KINDS (`auto_reply`, `out_of_office`), which must render a chip but are not
 * intents you filter by.
 *
 * There is no `unanalyzed` (NULL intent) option. Every reply with a body is
 * classified at arrival, so a NULL intent means a bounce/complaint notice or an
 * empty body — neither has an intent to find. Offering it next to "None" read
 * as a second, ambiguous no-intent choice. The count is still available as
 * `intent_breakdown.unanalyzed` from /inbox/stats.
 */
export const INTENT_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All intents' },
  { value: 'meeting_request', label: 'Meeting request' },
  { value: 'information_request', label: 'Information request' },
  { value: 'interested', label: 'Interested' },
  { value: 'objection', label: 'Objection' },
  { value: 'referral', label: 'Referral' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'unsubscribe', label: 'Unsubscribe' },
  // Stored as 'other'; the spec calls this bucket "None". This is the LLM's own
  // catch-all — it ran and declined to commit to one of the seven above.
  { value: 'other', label: 'None' },
]

/** Display label for a stored intent; unknown values are prettified, not dropped. */
export function intentLabel(stored: string | null | undefined): string | null {
  if (!stored) return null
  return INTENT_LABELS[stored] ?? stored.replace(/_/g, ' ')
}

/**
 * Intent chip styling. Intent has no good/bad ordering, so this deliberately
 * avoids the sentiment chips' tone ramp — actionable intents read indigo,
 * blockers amber, exits red, and the catch-all stays neutral. Without that
 * separation two chips on one card would look like one scale.
 */
export const INTENT_CHIP_CLASS: Record<string, string> = {
  meeting_request:
    'bg-[#5e6ad2]/10 text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30',
  information_request:
    'bg-[#7170ff]/10 text-[#5e5ce0] border-[#7170ff]/25 dark:bg-[#7170ff]/[0.14] dark:text-[#b4b3ff] dark:border-[#7170ff]/30',
  interested:
    'bg-[#7170ff]/10 text-[#5e5ce0] border-[#7170ff]/25 dark:bg-[#7170ff]/[0.14] dark:text-[#b4b3ff] dark:border-[#7170ff]/30',
  referral:
    'bg-cyan-500/10 text-cyan-700 border-cyan-500/25 dark:bg-cyan-500/[0.12] dark:text-cyan-400 dark:border-cyan-500/25',
  objection:
    'bg-amber-500/10 text-amber-700 border-amber-500/25 dark:bg-amber-500/[0.12] dark:text-amber-400 dark:border-amber-500/25',
  not_interested:
    'bg-red-500/10 text-red-700 border-red-500/25 dark:bg-red-500/[0.12] dark:text-red-400 dark:border-red-500/25',
  unsubscribe:
    'bg-red-500/10 text-red-700 border-red-500/25 dark:bg-red-500/[0.12] dark:text-red-400 dark:border-red-500/25',
}

const NEUTRAL_CHIP =
  'bg-muted/60 text-muted-foreground border-border/50 dark:bg-white/[0.04] dark:text-[#8a8f98] dark:border-white/[0.06]'

/** Chip classes for a stored intent; anything unstyled falls back to neutral. */
export function intentChipClass(stored: string | null | undefined): string {
  if (!stored) return NEUTRAL_CHIP
  return INTENT_CHIP_CLASS[stored] ?? NEUTRAL_CHIP
}
